import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Trophy, Edit3, Trash2 } from 'lucide-react';
import { confirmAction } from '@/lib/confirm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const { isAdmin } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editResult, setEditResult] = useState(null);
  const [editScores, setEditScores] = useState({ team1: '', team2: '' });

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const { data } = await axios.get(`${API}/results`);
      setResults(data);
    } catch (err) {
      console.error('Error fetching results:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditResult = async (e) => {
    e.preventDefault();
    if (!editResult) return;
    try {
      const body = {};
      if (editScores.team1 !== '') body.team1_score = parseInt(editScores.team1);
      if (editScores.team2 !== '') body.team2_score = parseInt(editScores.team2);
      // Determine winner by higher score
      if (body.team1_score !== undefined && body.team2_score !== undefined) {
        body.winner_id = body.team1_score >= body.team2_score ? editResult.team1_id : editResult.team2_id;
      }
      await axios.put(`${API}/admin/results/${editResult.tournament_id}/${editResult.match_id}`, body);
      toast.success('Result updated!');
      setEditResult(null);
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeleteResult = async (r) => {
    if (!(await confirmAction(`Remove result for ${r.tournament_name}?`, { confirmLabel: 'Remove' }))) return;
    try {
      await axios.delete(`${API}/admin/results/${r.tournament_id}/${r.match_id}`);
      toast.success('Result removed');
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const openEdit = (r) => {
    setEditResult(r);
    setEditScores({
      team1: r.team1_score?.toString() || '',
      team2: r.team2_score?.toString() || '',
    });
  };

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="results-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">Tournament</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-4">
            Results
          </h1>
          <p className="text-zinc-400">Finals scores and tournament winners</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800" data-testid="no-results">
            <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Results Yet</h2>
            <p className="text-zinc-500">Tournament finals haven't been completed yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {results.map((r, idx) => (
              <div key={r.match_id || idx} className="bg-[#121212] border border-zinc-800 overflow-hidden" data-testid={`result-${idx}`}>
                <div className="px-6 py-3 bg-[#1A1A1A] border-b border-zinc-800 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">{r.tournament_name}</p>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(r)} className="text-zinc-500 hover:text-cyan-400 transition-colors" data-testid={`edit-result-${idx}`}>
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteResult(r)} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-result-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-center gap-6">
                    <div className={`flex-1 text-right ${r.winner_name === r.team1_name ? '' : 'opacity-50'}`}>
                      <p className="text-white font-heading text-xl font-bold uppercase">{r.team1_name || 'TBD'}</p>
                      <p className="text-3xl font-mono font-bold text-cyan-400 mt-1">{r.team1_score ?? '-'}</p>
                    </div>
                    <div className="flex flex-col items-center px-4">
                      <span className="text-zinc-600 text-xs uppercase tracking-wider">vs</span>
                    </div>
                    <div className={`flex-1 text-left ${r.winner_name === r.team2_name ? '' : 'opacity-50'}`}>
                      <p className="text-white font-heading text-xl font-bold uppercase">{r.team2_name || 'TBD'}</p>
                      <p className="text-3xl font-mono font-bold text-cyan-400 mt-1">{r.team2_score ?? '-'}</p>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-zinc-800 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                      <span className="text-yellow-500 font-heading font-bold uppercase text-sm">{r.winner_name} Wins</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Result Dialog */}
      <Dialog open={!!editResult} onOpenChange={() => setEditResult(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="edit-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Edit Result</DialogTitle>
          </DialogHeader>
          {editResult && (
            <form onSubmit={handleEditResult} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">{editResult.team1_name} Score</Label>
                <Input type="number" value={editScores.team1} onChange={(e) => setEditScores({...editScores, team1: e.target.value})} min="0" className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400" data-testid="edit-team1-score" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">{editResult.team2_name} Score</Label>
                <Input type="number" value={editScores.team2} onChange={(e) => setEditScores({...editScores, team2: e.target.value})} min="0" className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400" data-testid="edit-team2-score" />
              </div>
              <p className="text-zinc-600 text-xs">Winner is automatically set to the team with the higher score.</p>
              <Button type="submit" className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="save-result-btn">
                Update Result
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
